import React, { useEffect, useState } from "react";
import { logger } from "@fidget/common/utils";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { useStore } from "../../../models";
import { agentCtxResourceDataToSnapshot } from "../../../models/currentUser/UserProfile";
import { OIDCService } from "../../../services/OIDCService";
import { WelcomeToHeader } from "../components/welcomeToHeader";
import loginStyles from "../login/login.css";
import { LoginAuthorizingLoaderSection } from "./loginAuthorizingLoaderSection";
import { LoginErrorSection } from "./loginErrorSection";
import { useAgentServices } from "../../../models/agentServices";

enum StateType {
  Success,
  Error,
  Loading
}

interface SuccessfulState {
  type: StateType.Success;
  code: string;
}

interface ErrorState {
  type: StateType.Error;
}

interface LoadingState {
  type: StateType.Loading;
}

type State = SuccessfulState | ErrorState | LoadingState;

export const LoginOidcResponse = () => {
  const { currentUserStore } = useStore();
  const { spinnerService, oidcService } = useAgentServices();
  const [state, setState] = useState<State>({ type: StateType.Loading });

  useEffect(() => {
    const params = new URL(window.location.href).searchParams;
    const code = params.get(OIDCService.responseType);
    if (code) {
      try {
        const { orgId, nonce } = oidcService.loadSavedParams();
        setState({ type: StateType.Success, code });

        spinnerService
          .authorizeOpenIdConnect({ orgId, nonce, code, redirectUri: OIDCService.redirectUri })
          .then((agentCtx) => {
            oidcService.clearSavedParams();
            currentUserStore.authenticateProfile(agentCtxResourceDataToSnapshot(agentCtx));
          })
          .catch((e) => {
            logger.error({ message: "Couldn't authorize with code", error: e });
            setState({ type: StateType.Error });
          });
      } catch (e) {
        logger.error({ message: "Couldn't load saved oicd params about", error: e });
        setState({ type: StateType.Error });
      }
    } else {
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      logger.error(`Didn't find code on OIDC response, errors: ${error}, ${errorDescription}`);

      setState({ type: StateType.Error });
    }
  }, []);

  const render = () => {
    switch (state.type) {
      case StateType.Success:
      case StateType.Loading:
        return <LoginAuthorizingLoaderSection />;
      case StateType.Error:
        return <LoginErrorSection />;
      default:
        assertUnreachable(state);
        return <></>;
    }
  };

  return (
    <div className={loginStyles["login-background"]}>
      <WelcomeToHeader styleType="login">{render()}</WelcomeToHeader>
    </div>
  );
};
